import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { PageHeader } from '@gs/ui';
import { useAuth, GsRole, hasRole } from '@gs/auth';
import { ALL_APPS, COMING_APPS } from '../data/apps';
import type { AppDefinition } from '../types';
import { useFavorites } from '../hooks/useFavorites';
import { AppCard } from '../components/AppCard';
import { PwaInstallButton } from '../components/PwaInstallButton';
import { FilterBar, type HubFilter } from '../components/FilterBar';

function filterApps(filter: HubFilter) {
  if (filter === 'all') return ALL_APPS;
  if (filter === 'azure') return ALL_APPS.filter((a) => a.platform === 'azure');
  if (filter === 'firebase') return ALL_APPS.filter((a) => a.platform === 'firebase');
  if (filter === 'production') return ALL_APPS.filter((a) => a.status === 'prod');
  if (filter === 'dev') return ALL_APPS.filter((a) => a.status === 'dev');
  if (filter === 'poc') return ALL_APPS.filter((a) => a.status === 'poc');
  return ALL_APPS;
}

function hasUrl(a: AppDefinition) {
  return Boolean(a.url?.trim());
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="mb-3 mt-5 flex items-center gap-2 first:mt-0">
      <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-slate-400 dark:text-slate-500">
        {title}
      </span>
      <div className="h-px flex-1 bg-slate-100 dark:bg-slate-800" />
    </div>
  );
}

const GRID = 'grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(80px,96px))]';

export default function AppsPage() {
  const [activeFilter, setActiveFilter] = useState<HubFilter>('all');
  const { toggle, isFav } = useFavorites();
  const { user } = useAuth();
  const isAdmin = hasRole(user, GsRole.ADMIN);

  const filtered = useMemo(() => {
    const base = filterApps(activeFilter);
    return isAdmin ? base : base.filter((a) => !a.adminOnly);
  }, [activeFilter, isAdmin]);

  const { favs, azure, firebase, bientot } = useMemo(() => {
    const favList = filtered.filter((a) => isFav(a.id));
    const rest = filtered.filter((a) => !isFav(a.id));
    return {
      favs: favList,
      azure: rest.filter((a) => a.platform === 'azure' && hasUrl(a)),
      firebase: rest.filter((a) => a.platform === 'firebase' && hasUrl(a)),
      bientot: rest.filter((a) => !hasUrl(a)),
    };
  }, [filtered, isFav]);

  const segments = useMemo(() => {
    const out: { title: string; apps: AppDefinition[]; start: number }[] = [];
    let start = 0;
    if (favs.length > 0) {
      out.push({ title: 'Mes favoris', apps: favs, start });
      start += favs.length;
    }
    if (azure.length > 0) {
      out.push({ title: 'Azure', apps: azure, start });
      start += azure.length;
    }
    if (firebase.length > 0) {
      out.push({ title: 'Firebase / Terrain', apps: firebase, start });
      start += firebase.length;
    }
    if (bientot.length > 0) {
      out.push({ title: 'Bientôt disponible', apps: bientot, start });
    }
    return out;
  }, [favs, azure, firebase, bientot]);

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Applications"
        description="Accès rapide aux outils Groupe Signalisation."
        actions={<PwaInstallButton />}
      />

      <div className="flex gap-3 rounded-xl border border-sky-100 bg-sky-50/80 px-4 py-3 text-sm text-sky-900 dark:border-sky-900/40 dark:bg-sky-950/40 dark:text-sky-100">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-sky-600 dark:text-sky-400" />
        <p>
          Cliquez l’étoile sur une application pour l’ajouter à votre tableau de bord personnel (page
          d’accueil).
        </p>
      </div>

      <FilterBar activeFilter={activeFilter} onChange={setActiveFilter} />

      {segments.map((seg) => (
        <section key={seg.title} aria-label={seg.title}>
          <SectionHeader title={seg.title} />
          <div className={GRID}>
            {seg.apps.map((app, i) => (
              <AppCard
                key={app.id}
                app={app}
                showStar
                isFav={isFav(app.id)}
                onToggleFav={() => toggle(app.id)}
                index={seg.start + i}
                userEmail={user?.email}
              />
            ))}
          </div>
        </section>
      ))}

      <section>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          À venir
        </h3>
        <div className="flex flex-wrap gap-2">
          {COMING_APPS.map((c) => (
            <span
              key={c.id}
              className="rounded-full border border-dashed border-slate-300 bg-slate-50 px-3 py-1.5 text-xs text-slate-600 dark:border-slate-600 dark:bg-slate-800/50 dark:text-slate-300"
              title={c.desc}
            >
              {c.name}
            </span>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 pt-6 text-center text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
        GS-Apps · Groupe Signalisation 2026
      </footer>
    </div>
  );
}
